import { useCallback, useMemo } from 'react'

import { useI18n } from '../../i18n/providers/I18nProvider.js'
import { Chart } from '../components/Chart.js'
import type { ChartProps } from '../types/index.js'
import { AnalyticsFeeder } from '../utils/analyticsFeeder.js'
import { SecondaryBarColors } from '../utils/constants.js'
import type { AnalyticsQuery } from '../utils/generateAnalyticsQuery.js'
import {
  generateAnalyticsQuery,
  generateTimeFrame,
  generateGroupBy,
} from '../utils/generateAnalyticsQuery.js'
import { getValueFromAnalyticsData } from '../utils/parser.js'

export const ActivityOverTimeBar = ({
  scope,
  id,
  timeFrame,
  className,
  segment,
}: ChartProps) => {
  const { $t } = useI18n()
  const createQueries = useCallback(() => {
    const generalQuery: AnalyticsQuery = {
      selects: [],
      timeFrame: generateTimeFrame({ timeFrame }),
      where: [
        { field: scope, value: id },
        { field: 'space_type', in: ['GROUP', 'BROADCAST'] },
        ...(segment?.where ? [segment.where] : []),
      ],
      limit: timeFrame.xAxisCount + 2,
    }

    const currentQuery: AnalyticsQuery = {
      ...generalQuery,
      selects: [
        {
          field: 'count(post) + count(reply) + count(react)',
          as: 'Activities',
        },
        { field: 'count(post)', as: 'Posts' },
        { field: 'count(reply)', as: 'Replies' },
        { field: 'count(react)', as: 'Reactions' },
      ],
    }

    const prevQuery: AnalyticsQuery = {
      ...currentQuery,
      timeFrame: generateTimeFrame({ timeFrame, target: 'previous' }),
    }

    const chartQuery: AnalyticsQuery = {
      ...currentQuery,
      groupBy: {
        ...generateGroupBy(timeFrame.granularity),
      },
      order: { by: 'timeBucket', dir: 'asc' },
    }

    if (timeFrame.id === 'allTime') {
      return [
        generateAnalyticsQuery(chartQuery),
        generateAnalyticsQuery(currentQuery),
      ]
    }

    return [
      generateAnalyticsQuery(chartQuery),
      generateAnalyticsQuery(currentQuery),
      generateAnalyticsQuery(prevQuery),
    ]
  }, [id, scope, timeFrame, segment])

  const queries = useMemo(() => [createQueries()], [createQueries])

  return (
    <AnalyticsFeeder queries={queries[0]}>
      {({ data, isLoading }) => (
        <Chart
          type="Bar"
          title={$t(
            {
              id: 'Analytics.ActivityOverTimeChartTitle',
              defaultMessage: 'Activity over time ({granularity})',
            },
            { granularity: timeFrame.granularityText },
          )}
          description={timeFrame.text}
          toolTipText={$t({
            id: 'Analytics.ActivityOverTimeBar.TooltipText',
            defaultMessage: 'Sum of posts, reactions and replies over time',
            description: 'Tooltip text for activity over time chart',
          })}
          current={getValueFromAnalyticsData(data[1], 'Activities')}
          previous={getValueFromAnalyticsData(data[2], 'Activities')}
          data={data[0]}
          syncId="analytics"
          timeFrame={timeFrame}
          indexBy={['Posts', 'Replies', 'Reactions']}
          labels={[
            $t({
              id: 'Generics.Posts',
              defaultMessage: 'Posts',
            }),
            $t({
              id: 'Generics.Replies',
              defaultMessage: 'Replies',
            }),
            $t({
              id: 'Generics.Reactions',
              defaultMessage: 'Reactions',
            }),
          ]}
          ColorTheme={SecondaryBarColors}
          isLoading={isLoading}
          className={className}
        />
      )}
    </AnalyticsFeeder>
  )
}
